import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { IconButton } from '@/components/primitives/icon-button'
import { cn } from '@/lib/cn'

export interface ActionMenuItem {
  disabled?: boolean
  iconSrc?: string
  key: string
  label: string
  onSelect: () => void
  tooltip?: string
  variant?: 'danger' | 'default' | 'success'
}

export interface ActionMenuProps {
  ariaLabel: string
  items: ActionMenuItem[]
}

function MoreIcon() {
  return (
    <svg aria-hidden className="size-4" fill="currentColor" viewBox="0 0 16 16">
      <circle cx="3.5" cy="8" r="1.2" />
      <circle cx="8" cy="8" r="1.2" />
      <circle cx="12.5" cy="8" r="1.2" />
    </svg>
  )
}

function ActionMenuIcon({ src }: { src: string }) {
  return (
    <img
      alt=""
      aria-hidden
      className="size-3.5 shrink-0"
      data-testid="action-menu-icon"
      src={src}
    />
  )
}

const actionMenuVariantClassName: Record<NonNullable<ActionMenuItem['variant']>, string> = {
  danger: 'text-[var(--color-danger)]',
  default: 'text-[var(--color-text)]',
  success: 'text-[var(--color-success)]',
}

interface ActionMenuPosition {
  left: number
  maxHeight: number
  top: number
}

const actionMenuViewportPadding = 8
const actionMenuTriggerGap = 6

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max)
}

function getActionMenuPosition(triggerRect: DOMRect, menuRect: DOMRect): ActionMenuPosition {
  const availableHeight = Math.max(0, window.innerHeight - actionMenuViewportPadding * 2)
  const renderedMenuHeight = Math.min(menuRect.height, availableHeight)
  const maxLeft = Math.max(
    actionMenuViewportPadding,
    window.innerWidth - menuRect.width - actionMenuViewportPadding,
  )
  const preferredLeft = triggerRect.right - menuRect.width
  const belowTop = triggerRect.bottom + actionMenuTriggerGap
  const aboveTop = triggerRect.top - renderedMenuHeight - actionMenuTriggerGap
  const roomBelow = window.innerHeight - actionMenuViewportPadding - belowTop
  const roomAbove = triggerRect.top - actionMenuTriggerGap - actionMenuViewportPadding
  const preferredTop =
    roomBelow >= renderedMenuHeight || roomBelow >= roomAbove ? belowTop : aboveTop
  const maxTop = Math.max(
    actionMenuViewportPadding,
    window.innerHeight - renderedMenuHeight - actionMenuViewportPadding,
  )

  return {
    left: clamp(preferredLeft, actionMenuViewportPadding, maxLeft),
    maxHeight: availableHeight,
    top: clamp(preferredTop, actionMenuViewportPadding, maxTop),
  }
}

export function ActionMenu({ ariaLabel, items }: ActionMenuProps) {
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<ActionMenuPosition | null>(null)

  useLayoutEffect(() => {
    if (!open) {
      return
    }

    const trigger = triggerRef.current
    const menu = menuRef.current

    if (!trigger || !menu) {
      return
    }

    // 先测量 portal 菜单，再决定向上或向下展开，避免表格底部行溢出 viewport。
    setPosition(
      getActionMenuPosition(trigger.getBoundingClientRect(), menu.getBoundingClientRect()),
    )
  }, [items.length, open])

  useEffect(() => {
    if (!open) {
      return undefined
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target

      if (!(target instanceof Node)) {
        return
      }

      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return
      }

      setOpen(false)
      setPosition(null)
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        setPosition(null)
        triggerRef.current?.focus()
      }
    }

    const handleScrollOrResize = () => {
      setOpen(false)
      setPosition(null)
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    window.addEventListener('resize', handleScrollOrResize)
    window.addEventListener('scroll', handleScrollOrResize, true)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('resize', handleScrollOrResize)
      window.removeEventListener('scroll', handleScrollOrResize, true)
    }
  }, [open])

  return (
    <>
      <IconButton
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={ariaLabel}
        onClick={() => {
          if (open) {
            setOpen(false)
            setPosition(null)
            return
          }

          setPosition(null)
          setOpen(true)
        }}
        ref={triggerRef}
        size="sm"
      >
        <MoreIcon />
      </IconButton>
      {open
        ? createPortal(
            <div
              className="fixed z-[110] w-max min-w-[132px] max-w-[calc(100vw-16px)] overflow-y-auto rounded-[7px] border border-[var(--color-panel-border)] bg-white p-1.5 shadow-[0_0_10px_rgba(26,28,25,0.1)]"
              ref={menuRef}
              role="menu"
              style={{
                left: position?.left ?? 0,
                maxHeight: position?.maxHeight,
                top: position?.top ?? 0,
                visibility: position ? 'visible' : 'hidden',
              }}
            >
              {items.map((item) => (
                <span className="block" key={item.key} title={item.tooltip}>
                  <button
                    className={cn(
                      'flex h-8 w-full min-w-0 cursor-pointer items-center gap-3 overflow-hidden rounded-md px-2.5 text-left text-sm transition-colors hover:bg-[var(--color-hover-muted)] disabled:cursor-not-allowed disabled:opacity-50',
                      actionMenuVariantClassName[item.variant ?? 'default'],
                    )}
                    disabled={item.disabled}
                    data-action-menu-item={item.key}
                    onClick={() => {
                      setOpen(false)
                      setPosition(null)
                      triggerRef.current?.focus()
                      item.onSelect()
                    }}
                    role="menuitem"
                    type="button"
                  >
                    {item.iconSrc ? <ActionMenuIcon src={item.iconSrc} /> : null}
                    <span className="min-w-0 truncate whitespace-nowrap">{item.label}</span>
                  </button>
                </span>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
